import type { Feature, PlanTier } from "@/services/types";

/**
 * The lowest plan that grants each feature.
 *
 * <p>Mirrors the backend `PlanCatalog`, which stays the source of truth — the client never
 * decides access from this, only what to *say* about it. Several places used to write a plan
 * name into prose ("Round Robin events are a Professional feature"), so moving a feature between
 * tiers meant finding all of them. This is the one place that changes now.
 */
export const REQUIRED_TIER: Record<Feature, Extract<PlanTier, "PROFESSIONAL" | "TEAM">> = {
  // Single-host: one person, working alone.
  GROUP_EVENT: "PROFESSIONAL",
  BOOKING_FORMS: "PROFESSIONAL",
  EXPERIENCES: "PROFESSIONAL",
  CUSTOM_BRANDING: "PROFESSIONAL",
  // Needs more than one host to mean anything.
  ROUND_ROBIN_EVENT: "TEAM",
  COLLECTIVE_EVENT: "TEAM",
  TEAMS: "TEAM",
  // Included with Team, and buyable as an option on Professional — so the tier named here is
  // the cheapest plan that *includes* it, not the only way to get it. Copy that offers the
  // option should say so rather than sending a solo host to a team plan.
  WHITE_LABEL: "TEAM",
};

/** The plan name shown to customers for a tier. */
export const TIER_LABEL: Record<PlanTier, string> = {
  FREE: "Free",
  PROFESSIONAL: "Professional",
  TEAM: "Team",
  // Enterprise mirrors Team in the catalog and is not sold separately yet.
  ENTERPRISE: "Team",
};

/** The plan a visitor must be on to use this feature, by name. */
export function planNameFor(feature: Feature): string {
  return TIER_LABEL[REQUIRED_TIER[feature]];
}

/**
 * Whether this feature can be bought as an option on Professional rather than only by moving
 * to Team. Today that is white-label alone: $3 on top of Professional buys our mark off the
 * pages a solo host shares, so telling them to buy a team plan would be wrong.
 */
export function isProfessionalOption(feature: Feature): boolean {
  return feature === "WHITE_LABEL";
}
